import { useEffect, useMemo, useRef } from 'react';
import { useCollectionStore } from '@stores/useCollectionStore';

const EMPTY_NODES = [];

/**
 * Extracts all node IDs recursively from a tree structure.
 * @param {Array} nodes
 * @returns {string[]}
 */
const getAllNodeIds = nodes => {
	return nodes.flatMap(node => [
		node.key,
		...(node.children?.length ? getAllNodeIds(node.children) : []),
	]);
};

/**
 * Extracts group node IDs (nodes with children), recursively.
 * @param {Array} nodes
 * @returns {string[]}
 */
const getGroupNodeIds = nodes => {
	return nodes.flatMap(node =>
		node.children?.length ? [node.key, ...getGroupNodeIds(node.children)] : []
	);
};

/**
 * Hook that initializes the Zustand store when collection data arrives.
 *
 * On first load: expands all groups and checks all nodes.
 * On collection switch or unmount: resets the store.
 *
 * @param {object|null} data - The collection data from useCollectionData
 * @param {string} [collectionUuid] - The currently selected collection UUID
 */
const useCollectionTree = (data, collectionUuid) => {
	// Zustand actions are stable references (never change identity),
	// so they are safe in the dependency array without causing re-runs.
	const expandAll = useCollectionStore(s => s.expandAll);
	const checkAll = useCollectionStore(s => s.checkAll);
	const reset = useCollectionStore(s => s.reset);
	const setObjects = useCollectionStore(s => s.setObjects);

	const nodes = data?.objects ?? EMPTY_NODES;
	const initializedIdRef = useRef(null);

	const allNodeIds = useMemo(() => getAllNodeIds(nodes), [nodes]);
	const groupNodeIds = useMemo(() => getGroupNodeIds(nodes), [nodes]);

	// Clear the previous collection's legend/features/tree state the moment the
	// selection changes, rather than waiting for the new fetch to resolve.
	// Without this, `data` (and therefore `nodes`) stays null while the new
	// collection loads, the sync effect below never runs, and the old
	// collection's objects keep showing in the map legend until new data arrives.
	const prevCollectionUuidRef = useRef(collectionUuid);
	useEffect(() => {
		if (prevCollectionUuidRef.current === collectionUuid) return;
		prevCollectionUuidRef.current = collectionUuid;
		initializedIdRef.current = null;
		reset();
	}, [collectionUuid, reset]);

	useEffect(() => {
		if (!nodes.length) return;

		// Skip re-initialization of expand/check state if we already set up for
		// this collection. Preserves user's selections across background refetches.
		if (initializedIdRef.current !== data?.uuid) {
			initializedIdRef.current = data?.uuid;
			expandAll(groupNodeIds);
			checkAll(allNodeIds);
		}

		// Always sync objects — color/label changes must reflect immediately
		// in the map legend without requiring a collection switch.
		setObjects(nodes);
	}, [data?.uuid, expandAll, checkAll, setObjects, allNodeIds, groupNodeIds, nodes]);

	// Reset store only on unmount — not on dependency changes.
	// This preserves user selections when the effect re-runs due to refetches.
	useEffect(() => {
		return () => {
			initializedIdRef.current = null;
			reset();
		};
	}, [reset]);
};

export default useCollectionTree;
